import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, KeyRound, Trash2, Fingerprint, ShieldAlert, IdCard } from "lucide-react";
import { toast } from "sonner";
import BusinessCardForm from "@/components/BusinessCardForm";

function EditCardDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-gray-500 hover:text-[#1B4332]" title="Edit business card" data-testid={`edit-card-${user.id}`}>
          <IdCard className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Business card — {user.name}</DialogTitle>
        </DialogHeader>
        <BusinessCardForm
          initial={user.profile || {}}
          saveFn={(payload) => api.patch(`/admin/users/${user.id}/profile`, payload)}
          onSaved={() => { setOpen(false); onDone?.(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function CreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    if (form.password.length < 8) return toast.error("Temporary password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "member" });
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#0A0A0A] hover:bg-gray-800" data-testid="add-user-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={form.name} data-testid="new-user-name"
              onChange={(e) => set("name")(e.target.value)} placeholder="Jane Doe" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={form.email} data-testid="new-user-email"
              onChange={(e) => set("email")(e.target.value)} placeholder="jane@company.com" />
          </div>
          <div className="space-y-1.5">
            <Label>Temporary password</Label>
            <Input value={form.password} data-testid="new-user-password"
              onChange={(e) => set("password")(e.target.value)} placeholder="Min 8 characters" />
            <p className="text-xs text-gray-400">User will be required to change this on first sign-in.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={set("role")}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="member" data-testid="role-member">Member</SelectItem>
                <SelectItem value="admin" data-testid="role-admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="create-user-submit"
            className="bg-[#0A0A0A] hover:bg-gray-800">{busy ? "Creating…" : "Create user"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pw.length < 8) return toast.error("Temporary password must be at least 8 characters");
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { password: pw });
      toast.success("Temporary password set");
      setOpen(false); setPw(""); onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`reset-pw-${user.id}`} title="Reset password">
          <KeyRound className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Reset password for {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label>Temporary password</Label>
          <Input value={pw} data-testid="reset-pw-input"
            onChange={(e) => setPw(e.target.value)} placeholder="Min 8 characters" />
          <p className="text-xs text-gray-400">User must change it on next sign-in.</p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="reset-pw-submit"
            className="bg-[#0A0A0A] hover:bg-gray-800">{busy ? "Saving…" : "Set password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error("Could not load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update role");
    }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove user");
    }
  };

  const clearPasskeys = async (u) => {
    try {
      const { data } = await api.delete(`/admin/users/${u.id}/passkeys`);
      toast.success(`Removed ${data.deleted} passkey(s) for ${u.name}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove passkeys");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Users &amp; roles</h1>
          <p className="text-sm text-gray-500 mt-1">Provision accounts and manage access.</p>
        </div>
        <CreateUserDialog onCreated={load} />
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">Loading users…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-4 px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-gray-400 border-b">
            <div>User</div><div>Role</div><div>Status</div><div className="text-right">Actions</div>
          </div>
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`}
              className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 sm:gap-4 items-center px-4 py-3 border-b last:border-0">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {u.name}
                  {u.id === me.id && <Badge variant="secondary" className="text-[10px]">You</Badge>}
                </div>
                <div className="text-xs text-gray-500 truncate">{u.email}</div>
              </div>
              <div>
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === me.id}>
                  <SelectTrigger className="h-8 w-[120px]" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {u.passkey_count > 0 && (
                  <Badge variant="outline" className="text-[10px] gap-1" data-testid={`passkey-badge-${u.id}`}>
                    <Fingerprint className="w-3 h-3" /> Passkey
                  </Badge>
                )}
                {u.must_change_password && (
                  <Badge className="text-[10px] gap-1 bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`pending-pw-${u.id}`}>
                    <ShieldAlert className="w-3 h-3" /> Temp password
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-end gap-1">
                <EditCardDialog user={u} onDone={load} />
                <ResetPasswordDialog user={u} onDone={load} />
                {u.passkey_count > 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700" title="Clear passkeys" data-testid={`clear-passkeys-${u.id}`}>
                        <Fingerprint className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Clear passkeys for {u.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes all {u.passkey_count} registered passkey(s) for this user. They will be
                          prompted to enroll a new passkey (or can sign in with their password if enrollment is not enforced). This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => clearPasskeys(u)} data-testid={`confirm-clear-passkeys-${u.id}`}
                          className="bg-amber-600 hover:bg-amber-700">Clear passkeys</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {u.id !== me.id && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently deletes the account and its passkeys. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => removeUser(u)} data-testid={`confirm-delete-${u.id}`}
                          className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
